import { cn } from '@shared/cove-ui';
import { Facebook, Instagram, LogoSecondary, Youtube } from '../../Icons';
import { LayoutVariant } from '../Layout';
import SubscribeForm from './subscribeForm';
import { FooterLink, headerTranslationKeys, linkHeaders } from './links';
import { useTranslation } from 'react-i18next';

const DesktopFooter: React.FC<{
  links: FooterLink;
  onSubscribe: (email: string) => Promise<{ status: string }>;
  isMinimal?: boolean;
  variant?: LayoutVariant;
}> = ({ links, onSubscribe, isMinimal = false, variant, ...props }) => {
  const { t } = useTranslation('common');

  return (
    <div
      {...props}
      className="pt-cove-75 px-cove-50 mx-auto hidden w-full max-w-[1396px] flex-col pb-[70px] md:flex"
    >
      {isMinimal ? (
        <div className="sm:py-cove-50 md:px-cove-87 md:py-cove-50 gap-cove-25 m-0 flex flex-col-reverse justify-between p-0 md:flex-row md:gap-0">
          <div className="gap-cove-15 flex flex-wrap items-center text-sm font-light md:flex-nowrap">
            <a href="/" className="flex items-center no-underline">
              <LogoSecondary
                color={variant === 'activate' ? '#3ebeb0' : '#002334'}
              />
              <span className="sr-only">Go back to covesmart.com homepage</span>
            </a>
            <div>
              <p className="text-cove-navy-blue m-0 text-sm font-extralight">
                © {new Date().getFullYear()} Cove Smart, LLC{' '}
                {t('links.all-rights-reserved', {
                  defaultValue: 'All rights reserved.',
                })}
              </p>
              <p className="text-cove-navy-blue m-0 text-sm font-extralight">
                14015 Minuteman Drive, Draper, UT 84020
              </p>
            </div>
          </div>
          <div>
            {variant === 'activate' ? (
              <div>
                <p className="mt-cove-15 text-base text-white md:mt-0">
                  Follow us <span className="underline">@covesmart</span>
                </p>
                <div className="gap-cove-15 mt-cove-10 flex justify-start md:justify-end">
                  <a
                    href="https://www.instagram.com/covesmart/"
                    className="no-underline"
                  >
                    <Instagram color="#002334" />
                    <span className="sr-only">Instagram</span>
                  </a>
                  <a
                    href="https://www.facebook.com/covesmart/"
                    className="no-underline"
                  >
                    <Facebook color="#002334" />
                    <span className="sr-only">Facebook</span>
                  </a>
                  <a
                    href="https://www.youtube.com/channel/UC0i0Rkot3hmtOyBXhvo3PEw?view_as=subscriber"
                    className="no-underline"
                  >
                    <Youtube color="#002334" />
                    <span className="sr-only">Youtube</span>
                  </a>
                </div>
              </div>
            ) : (
              <SubscribeForm
                handleSubscribe={onSubscribe}
                style={{ marginTop: 0 }}
              />
            )}
          </div>
        </div>
      ) : (
        <>
          <div
            className={cn(
              'gap-cove-15 lg:gap-cove-35 xl:gap-cove-50 flex w-full flex-col justify-between md:flex-row',
              variant === 'activate' && 'text-white'
            )}
          >
            {linkHeaders.map((h, i) => {
              const translationKey = headerTranslationKeys[h];
              const translatedHeader = t(`links.${translationKey}`, {
                defaultValue: h,
              });
              if (h === 'Environmental Detectors') return null;
              return (
                <div key={`footer-link-header-${i}`}>
                  <h5 className="mb-cove-15 text-base font-bold">
                    {translatedHeader}
                  </h5>
                  <div className="gap-cove-15 flex flex-col">
                    {links[h].map(l => {
                      const translatedLabel = t(`links.${l.translationKey}`, {
                        defaultValue: l.label,
                      });
                      return (
                        <a
                          key={`${l.to}-${i}`}
                          href={l.to}
                          className="text-left text-sm font-light leading-[14px]"
                        >
                          {translatedLabel}
                        </a>
                      );
                    })}
                  </div>
                  {i === 0 && (
                    <>
                      <h5 className="mb-cove-15 mt-cove-25 text-base font-bold">
                        {t(
                          `links.${headerTranslationKeys[linkHeaders[i + 1]]}`,
                          {
                            defaultValue: linkHeaders[i + 1],
                          }
                        )}
                      </h5>
                      <div className="gap-cove-15 flex flex-col">
                        {links[linkHeaders[i + 1]].map(l => {
                          const translatedLabel = t(
                            `links.${l.translationKey}`,
                            {
                              defaultValue: l.label,
                            }
                          );
                          return (
                            <a
                              key={`${l.to}`}
                              href={l.to}
                              className="text-left text-sm font-light leading-[14px]"
                            >
                              {translatedLabel}
                            </a>
                          );
                        })}
                      </div>
                    </>
                  )}
                  {i === linkHeaders.length - 1 && (
                    <SubscribeForm handleSubscribe={onSubscribe} />
                  )}
                </div>
              );
            })}
          </div>
          <div
            className={cn(
              'pt-cove-25 md:border-t-cove-navy-blue gap-cove-25 border-top-0 m-0 flex flex-col-reverse justify-between md:mt-[56px] md:flex-row md:gap-0 md:border-t md:pt-[30px]',
              variant === 'activate' && 'text-white'
            )}
          >
            <div className="gap-cove-15 flex flex-wrap items-center text-sm font-light md:flex-nowrap">
              <a href="/" className="flex items-center no-underline">
                <LogoSecondary
                  color={variant === 'activate' ? '#ffffff' : '#002334'}
                />
              </a>
              <a href="/legal/" className="no-underline">
                Legal
              </a>
              <a href="/privacy-policy/" className="no-underline">
                Privacy
              </a>
              <a href="/state-licensing/" className="no-underline">
                State Licenses
              </a>
              <p className="mb-0 text-sm font-light">
                © {new Date().getFullYear()} Cove Smart, LLC{' '}
                {t('links.all-rights-reserved', {
                  defaultValue: 'All rights reserved.',
                })}{' '}
                14015 Minuteman Drive, Draper, UT 84020
              </p>
            </div>
            <div className="gap-cove-15 flex flex-wrap items-center text-sm font-light md:flex-nowrap">
              <a
                href="https://www.instagram.com/covesmart/"
                className="no-underline"
              >
                <Instagram color="#002334" />
                <span className="sr-only">Instagram</span>
              </a>
              <a
                href="https://www.facebook.com/covesmart/"
                className="no-underline"
              >
                <Facebook color="#002334" />
                <span className="sr-only">Facebook</span>
              </a>
              <a
                href="https://www.youtube.com/channel/UC0i0Rkot3hmtOyBXhvo3PEw?view_as=subscriber"
                className="no-underline"
              >
                <Youtube color="#002334" />
                <span className="sr-only">Youtube</span>
              </a>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DesktopFooter;
